// pfs-data-table.ts — generische Listen-Tabelle (PFS-491, Epic PFS-52/591).
//
// Loest das ueber die Cut-App-Listen (admin-users, practices, specialties,
// members, strikes) ~5x kopierte buildTable()-Muster ab: umrandete Card, dezent
// getoenter Header, Zeilen-Trenner via border-top (Matrix-Leitbild MED-77) statt
// Voll-Borders, deleted-Row-Muting, Hover, plus Loading- und Empty-Zustand.
// KEIN WC-pro-Row (ADR-0001 Perf): ALLE Zeilen rendern in EINEM Shadow.
//
// Bewusst FOKUSSIERT: Card-Huelle, <pfs-card-header>, Toolbar (Scope-Toggle +
// Suche + Anlegen-Button) und der Daten-Fetch bleiben Consumer-Sache — dafuer
// gibt es bereits eigene Primitive. Diese WC ist NUR die Tabelle + ihre Zustaende.
//
// Der Zellinhalt kommt vom Consumer via column.cell(item) => Node | string. Das
// deckt generisch ALLES ab: Text, Multi-Line-Zellen (.cell-stack), custom Grafik
// (Strikes-Severity-SVG), Inline-Controls (Members-Rollen-Select) UND die
// Aktions-Spalte (cell liefert ein <pfs-row-actions>). Business-Logik, Modals und
// Confirm-Flows bleiben in der Host-App.
//
// API (Properties, live — Setter re-rendert):
//   el.columns = DataColumn[]  — {key, label, align?, width?, cell}
//     cell(item) => Node | string  (string => textContent; XSS-sicher)
//   el.items = T[]
//   el.rowClass?(item) => string   — CSS-Klasse(n) je <tr>, z.B. "deleted-row"
//   el.loading = boolean           — Spinner statt Tabelle
//   el.emptyIcon = string          — SVG-Markup fuers Empty-Icon (sonst Default)
// Attribute (data-*, reaktiv):
//   data-loading-text   Text unter dem Spinner
//   data-empty-title    Titelzeile des Leerzustands
//   data-empty-message  optionale Sekundaerzeile
//   data-empty-variant  card (Default) | plain
//   data-count          optionaler Count-Badge am ersten Spalten-Header
//                       (<pfs-badge data-count>, z.B. "MITGLIED 23"). Der Count
//                       gehoert zur Liste, nicht in den Card-Header (PFS-626).
//   data-has-more       gesetzt → es gibt weitere Seiten. Ein Sentinel am
//                       Listenende feuert beim Sichtbarwerden `pfs:load-more`
//                       (bubbles+composed). Der Consumer laedt die naechste Seite
//                       (offset += limit) und HAENGT an (el.items = [...alt, ...neu]),
//                       setzt data-has-more neu (Total > items.length). PFS-631.
//   data-loading-more   gesetzt → Nachlade-Hinweis + unterdrueckt weitere
//                       load-more-Fires bis der Consumer es wieder entfernt.
// Events: `pfs:load-more` (bubbles+composed) — Infinite-Scroll-Trigger.
//
// Cell-Helper-Klassen (im WC-Shadow, fuer das column.cell-DOM nutzbar):
//   .cell-stack  vertikaler Titel+Sub-Block  (= altes .uname)
//   .cell-sub    leiser, kleiner Sub-Text     (= altes .sub)
//
// Events: das von einem eingebetteten <pfs-row-actions> gefeuerte pfs:action
//   bubbelt composed durch die Tabelle hindurch.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import "../badge/pfs-badge.js";

const STYLES = `
:host { display: block; }
.dt-card { border: 1px solid var(--pfs-color-outline); border-radius: 0.5rem; overflow: hidden; }
.dt-scroll { overflow-x: auto; }
table.dt { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
table.dt thead { background: var(--pfs-color-surface-sunken); }
table.dt th { text-align: left; padding: 0.5rem 1rem; color: var(--pfs-color-on-surface-muted); font-weight: 600; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; }
table.dt th.dt-right { text-align: right; }
table.dt th.dt-center { text-align: center; }
table.dt td { padding: 0.55rem 1rem; color: var(--pfs-color-on-surface); vertical-align: middle; }
table.dt td.dt-right { text-align: right; white-space: nowrap; }
table.dt td.dt-center { text-align: center; }
table.dt tbody tr { border-top: 1px solid var(--pfs-color-outline); }
table.dt tbody tr:hover { background: var(--pfs-color-surface-hover); }
/* Soft-deleted (nur Admin-Sicht, PFS-632): die Zeile ist an JEDER Stelle als
   gelöscht erkennbar — zarter, halbtransparenter Danger-Wash (theme-adaptiv via
   color-mix, kein opacity → Aktionen faden nicht aus) + gedimmter Text + subtiler
   Danger-Balken links. BEWUSST KEINE Durchstreichung: eine einzelne durchgestrichene
   Spalte wirkt schief, die ganze Zeile wäre schlecht lesbar — der Wash trägt die
   Erkennbarkeit. Aktions-Spalte bleibt normal/klickbar. */
table.dt tbody tr.deleted-row { background: color-mix(in srgb, var(--pfs-color-danger) 6%, transparent); }
table.dt tbody tr.deleted-row:hover { background: color-mix(in srgb, var(--pfs-color-danger) 11%, transparent); }
table.dt tbody tr.deleted-row td { color: var(--pfs-color-on-surface-muted); }
table.dt tbody tr.deleted-row td:first-child { box-shadow: inset 3px 0 0 var(--pfs-color-danger); }
/* Zell-Helper fuer das column.cell-DOM (liegt in DIESEM Shadow) */
.cell-stack { display: flex; flex-direction: column; gap: 0.05rem; }
.cell-sub { color: var(--pfs-color-on-surface-muted); font-size: 0.75rem; }
/* Loading: zentraler main-Spinner, min-height gibt Raum zum Zentrieren. */
.dt-loading { min-height: 14rem; }
/* Infinite-Scroll-Sentinel: unsichtbarer Trigger am Listenende (PFS-631).
   Beim Sichtbarwerden feuert pfs:load-more; data-loading-more zeigt hier den
   Nachlade-Hinweis. */
/* „Mehr laden"-Button = Infinite-Scroll-Sentinel UND Klick-Trigger (PFS-632).
   Voll-breit, dezent, mit Hover-Affordanz; beim Nachladen deaktiviert. */
.dt-more {
  display: block; width: 100%; box-sizing: border-box;
  padding: 0.6rem 1rem; text-align: center;
  font: inherit; font-size: 0.8rem;
  color: var(--pfs-color-on-surface-muted);
  background: none; border: none;
  border-top: 1px solid var(--pfs-color-outline);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.dt-more:hover:not(:disabled) { background: var(--pfs-color-surface-hover); color: var(--pfs-color-on-surface); }
.dt-more:focus-visible { outline: none; box-shadow: var(--pfs-shadow-focus-ring-strong, 0 0 0 3px rgba(37, 99, 235, 0.45)); }
.dt-more:disabled { cursor: default; }
`;

type Align = "left" | "right" | "center";

export interface DataColumn<T = unknown> {
  key: string;
  label: string;
  align?: Align;
  width?: string;
  cell: (item: T) => Node | string;
}

/** Typisierte Consumer-Sicht auf ein <pfs-data-table>-Element — generisch ueber den
 *  Row-Typ T. Consumer nutzen `createElement(...) as PfsDataTableElement<Row>` bzw.
 *  `querySelector<PfsDataTableElement<Row>>(...)` statt `as unknown as {...}`. Die
 *  data-* Attribute laufen ueber HTMLElement.dataset. */
export interface PfsDataTableElement<T> extends HTMLElement {
  columns: DataColumn<T>[];
  items: readonly T[];
  rowClass?: (item: T) => string;
  emptyIcon?: string;
  loading?: boolean;
  appendItems?(extra: T[]): void;
}

const alignClass = (a?: Align): string => (a === "right" ? " dt-right" : a === "center" ? " dt-center" : "");

defineComponent({
  tagName: "pfs-data-table",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const body = document.createElement("div");
    body.className = "dt-body";
    root.appendChild(body);

    let columns: DataColumn[] = [];
    let items: unknown[] = [];
    let loading = false;
    let rowClass: ((item: unknown) => string) | undefined;
    let emptyIcon = "";
    let io: IntersectionObserver | null = null; // Infinite-Scroll-Sentinel-Observer (PFS-631)

    const self = host as PfsDataTableElement<unknown>;
    const preColumns = takeOwnProperty<DataColumn[]>(host, "columns");
    const preItems = takeOwnProperty<unknown[]>(host, "items");
    const preLoading = takeOwnProperty<boolean>(host, "loading");
    const preRowClass = takeOwnProperty<(item: unknown) => string>(host, "rowClass");
    const preEmptyIcon = takeOwnProperty<string>(host, "emptyIcon");

    const buildLoading = (): Node => {
      const area = document.createElement("div");
      area.className = "dt-loading";
      const li = document.createElement("pfs-loading-indicator");
      li.setAttribute("data-variant", "main");
      const t = host.dataset.loadingText;
      if (t) li.setAttribute("data-text", t);
      area.appendChild(li);
      return area;
    };

    const buildEmpty = (): Node => {
      const empty = document.createElement("pfs-empty-state");
      if (host.dataset.emptyTitle) empty.dataset.title = host.dataset.emptyTitle;
      if (host.dataset.emptyMessage) empty.dataset.message = host.dataset.emptyMessage;
      empty.dataset.variant = host.dataset.emptyVariant ?? "card";
      if (emptyIcon) {
        const wrap = document.createElement("div");
        wrap.innerHTML = emptyIcon; // statisches App-SVG-Literal, kein User-Input
        const ico = wrap.firstElementChild;
        if (ico) {
          ico.setAttribute("slot", "icon");
          empty.appendChild(ico);
        }
      }
      return empty;
    };

    // Eine einzelne Zeile bauen — geteilt von buildTable (Voll-Render) und
    // appendItems (Infinite-Scroll-Nachschub ohne Voll-Repaint). PFS-631.
    const buildRow = (item: unknown): HTMLTableRowElement => {
      const tr = document.createElement("tr");
      const cls = rowClass?.(item);
      if (cls) tr.className = cls;
      for (const c of columns) {
        const td = document.createElement("td");
        const ac = alignClass(c.align).trim();
        if (ac) td.className = ac;
        if (c.width) td.style.width = c.width;
        const content = c.cell(item);
        if (typeof content === "string") td.textContent = content;
        else td.appendChild(content);
        tr.appendChild(td);
      }
      return tr;
    };

    const buildTable = (): Node => {
      const card = document.createElement("div");
      card.className = "dt-card";
      const scroll = document.createElement("div");
      scroll.className = "dt-scroll";
      const table = document.createElement("table");
      table.className = "dt";

      const thead = document.createElement("thead");
      const htr = document.createElement("tr");
      columns.forEach((c, i) => {
        const th = document.createElement("th");
        th.className = ("" + alignClass(c.align)).trim();
        th.textContent = c.label;
        if (c.width) th.style.width = c.width;
        // Count-Badge am ERSTEN Header-Feld — der Count gehoert zur Liste (PFS-626).
        // pfs-badge hat eigenes Shadow → th-uppercase/letter-spacing faerbt nicht ab.
        const cnt = host.dataset.count;
        if (i === 0 && cnt != null && cnt !== "") {
          const badge = document.createElement("pfs-badge");
          badge.setAttribute("data-count", cnt);
          badge.style.marginLeft = "0.5rem";
          th.appendChild(badge);
        }
        htr.appendChild(th);
      });
      thead.appendChild(htr);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");
      for (const item of items) tbody.appendChild(buildRow(item));
      table.appendChild(tbody);
      scroll.appendChild(table);
      card.appendChild(scroll);
      // Infinite-Scroll (PFS-631) + proaktiver Klick-Trigger (PFS-632): EIN
      // klickbarer „Mehr laden"-Button am Listenende. Er ist ZUGLEICH das
      // IntersectionObserver-Ziel (Auto-Load beim Reinscrollen) UND manuell
      // klickbar (proaktiv + Fallback, falls der Sentinel mal nicht feuert). Beim
      // Nachladen deaktiviert + „Lädt weitere…". Text via data-load-more-text.
      if (host.dataset.hasMore != null) {
        const more = document.createElement("button");
        more.type = "button";
        more.className = "dt-more";
        more.setAttribute("part", "load-more");
        const loadingMore = host.dataset.loadingMore != null;
        more.textContent = loadingMore
          ? (host.dataset.loadingMoreText ?? "Lädt weitere …")
          : (host.dataset.loadMoreText ?? "Mehr laden");
        more.disabled = loadingMore;
        more.addEventListener("click", fireLoadMore);
        card.appendChild(more);
      }
      return card;
    };

    // Ein load-more pro Sichtbarwerden; data-loading-more unterdrueckt Re-Fires,
    // bis der Consumer die naechste Seite geladen + das Attribut entfernt hat.
    const fireLoadMore = (): void => {
      if (host.dataset.loadingMore != null) return;
      host.dispatchEvent(new CustomEvent("pfs:load-more", { bubbles: true, composed: true }));
    };
    const wireSentinel = (): void => {
      if (io) {
        io.disconnect();
        io = null;
      }
      const sentinel = root.querySelector(".dt-more");
      // Kein Sentinel (kein data-has-more) oder Umgebung ohne IntersectionObserver
      // (SSR / manche Test-DOMs) → kein Auto-Load, aber der Rest rendert normal.
      if (!sentinel || typeof IntersectionObserver === "undefined") return;
      // rootMargin laedt schon kurz VOR dem Ende nach (weiches Scroll-Erlebnis).
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) if (e.isIntersecting) fireLoadMore();
        },
        { rootMargin: "200px" },
      );
      io.observe(sentinel);
    };

    const paint = (): void => {
      let content: Node;
      if (loading) content = buildLoading();
      else if (items.length === 0) content = buildEmpty();
      else content = buildTable();
      body.replaceChildren(content);
      wireSentinel();
    };

    Object.defineProperty(host, "columns", {
      configurable: true,
      get: () => columns,
      set: (v: unknown) => {
        columns = Array.isArray(v) ? (v as DataColumn[]) : [];
        paint();
      },
    });
    Object.defineProperty(host, "items", {
      configurable: true,
      get: () => items,
      set: (v: unknown) => {
        items = Array.isArray(v) ? v : [];
        paint();
      },
    });
    Object.defineProperty(host, "loading", {
      configurable: true,
      get: () => loading,
      set: (v: unknown) => {
        loading = !!v;
        paint();
      },
    });
    Object.defineProperty(host, "rowClass", {
      configurable: true,
      get: () => rowClass,
      set: (v: unknown) => {
        rowClass = typeof v === "function" ? (v as (item: unknown) => string) : undefined;
        paint();
      },
    });
    Object.defineProperty(host, "emptyIcon", {
      configurable: true,
      get: () => emptyIcon,
      set: (v: unknown) => {
        emptyIcon = v == null ? "" : String(v);
        paint();
      },
    });

    // appendItems — Infinite-Scroll-Nachschub: haengt die naechste Seite an das
    // bestehende <tbody> an OHNE Voll-Repaint (Scroll-Position bleibt). Consumer
    // ruft es im pfs:load-more-Handler. Fallback auf Voll-Render, falls (noch)
    // keine Tabelle da ist (leer/loading). PFS-631.
    self.appendItems = (extra) => {
      if (!Array.isArray(extra) || extra.length === 0) return;
      items = items.concat(extra);
      const tbody = root.querySelector("tbody");
      if (!tbody) {
        paint();
        return;
      }
      for (const item of extra) tbody.appendChild(buildRow(item));
      wireSentinel(); // Sentinel sitzt jetzt weiter unten → neu beobachten
    };

    if (preColumns !== undefined) self.columns = preColumns;
    if (preItems !== undefined) self.items = preItems;
    if (preLoading !== undefined) self.loading = preLoading;
    if (preRowClass !== undefined) self.rowClass = preRowClass;
    if (preEmptyIcon !== undefined) self.emptyIcon = preEmptyIcon;

    // Re-Paint bei Aenderung der reaktiven data-* (Empty-/Loading-Texte).
    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: [
        "data-loading-text",
        "data-empty-title",
        "data-empty-message",
        "data-empty-variant",
        "data-count",
        "data-has-more",
        "data-loading-more",
        "data-loading-more-text",
      ],
    });
    ctx.cleanup.push(() => obs.disconnect());
    ctx.cleanup.push(() => io?.disconnect());

    paint();
  },
});
